"use client";

import React, { useState, useEffect } from "react";
import { useApi } from "../../hooks/api/useApi";
import { Link, useNavigate } from "react-router-dom";
import { InputField, PasswordInput, PhoneInput } from "./components";
import { toast } from "react-toastify";

interface FormData {
  businessName: string;
  emailAddress: string;
  password: string;
  confirmPassword: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
}

interface FormErrors {
  businessName?: string;
  emailAddress?: string;
  password?: string;
  confirmPassword?: string;
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  terms?: string;
}

const Register: React.FC = () => {
  const { post, isLoading } = useApi();
  const [formData, setFormData] = useState<FormData>({
    businessName: "",
    emailAddress: "",
    password: "",
    confirmPassword: "",
    firstName: "",
    lastName: "",
    phoneNumber: "",
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [termsAgreed, setTermsAgreed] = useState<boolean>(false);
  const [isFormValid, setIsFormValid] = useState<boolean>(false);

  const navigate = useNavigate();

  // Validate form on every change to enable/disable submit button
  useEffect(() => {
    const validateFormFields = () => {
      const allFieldsFilled =
        formData.businessName.trim() !== "" &&
        formData.emailAddress.trim() !== "" &&
        formData.password.trim() !== "" &&
        formData.confirmPassword.trim() !== "" &&
        formData.firstName.trim() !== "" &&
        formData.lastName.trim() !== "" &&
        formData.phoneNumber.trim() !== "";

      const validEmail = /\S+@\S+\.\S+/.test(formData.emailAddress);
      const validPhone = /^\d{10,11}$/.test(formData.phoneNumber);
      const validPassword = formData.password.length >= 8;
      const passwordsMatch = formData.password === formData.confirmPassword;

      return (
        allFieldsFilled &&
        validEmail &&
        validPhone &&
        validPassword &&
        passwordsMatch &&
        termsAgreed
      );
    };

    setIsFormValid(validateFormFields());
  }, [formData, termsAgreed]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear error when user types
    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name as keyof FormErrors];
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.businessName.trim())
      newErrors.businessName = "Business name is required";
    if (!formData.firstName.trim())
      newErrors.firstName = "First name is required";
    if (!formData.lastName.trim()) newErrors.lastName = "Last name is required";

    if (!formData.emailAddress.trim()) {
      newErrors.emailAddress = "Email address is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.emailAddress)) {
      newErrors.emailAddress = "Email address is invalid";
    }

    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = "Phone number is required";
    } else if (!/^\d{10,11}$/.test(formData.phoneNumber)) {
      newErrors.phoneNumber = "Phone number must be 10-11 digits";
    }

    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (formData.password.length < 10) {
      newErrors.password = "Password must be at least 10 characters";
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    if (!termsAgreed) {
      newErrors.terms = "You must agree to the terms and conditions";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    // Format phone number correctly for backend
    let formattedPhone = formData.phoneNumber;
    if (formattedPhone.startsWith("0")) {
      formattedPhone = formattedPhone.substring(1);
    }

    // Create payload exactly as expected by backend
    const payload = {
      businessName: formData.businessName,
      emailAddress: formData.emailAddress,
      password: formData.password,
      confirmPassword: formData.confirmPassword,
      firstName: formData.firstName,
      lastName: formData.lastName,
      phoneNumber: `0${formattedPhone}`,
    };

    const response = await post("/access/signup", payload);

    if (response.statusCode === "00") {
      toast.success("Onboarded Successfully", {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
      });
      //   redirect to login
      setTimeout(() => {
        navigate("/login");
      }, 2000);
    } else if (response.statusCode === "02") {
      toast.error("You're already registered", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
      });
    } else {
      toast.error("Something went wrong. Please try again later.", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
      });
    }
  };

  return (
    <div>
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          Create your Vaspay account
        </h2>
      </div>

      <div className="mt-8 sm:mx-auto w-full sm:max-w-2xl">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <form className="space-y-6" onSubmit={handleSubmit}>
            {/* Business Name */}
            <InputField
              id="businessName"
              name="businessName"
              type="text"
              value={formData.businessName}
              onChange={handleChange}
              label="Business name"
              placeholder="Tom and Jerry Enterprises"
              error={errors.businessName}
              helperText="Ensure this name matches the name on your registration certificate."
            />

            {/* First Name, Last Name */}
            <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2">
              <InputField
                id="firstName"
                name="firstName"
                type="text"
                value={formData.firstName}
                onChange={handleChange}
                label="First name"
                placeholder="john"
                error={errors.firstName}
              />

              <InputField
                id="lastName"
                name="lastName"
                type="text"
                value={formData.lastName}
                onChange={handleChange}
                label="Last name"
                placeholder="doe"
                error={errors.lastName}
              />
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Please ensure this is first and last name on your Government ID
              document.
            </p>

            {/* Email Address */}
            <InputField
              id="emailAddress"
              name="emailAddress"
              type="email"
              value={formData.emailAddress}
              onChange={handleChange}
              label="Email address"
              placeholder="johndoe.ltd@company.com"
              error={errors.emailAddress}
            />

            {/* Phone Number */}
            <PhoneInput
              value={formData.phoneNumber}
              onChange={handleChange}
              error={errors.phoneNumber}
            />

            {/* Password */}
            <PasswordInput
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              label="Choose a password"
              placeholder="••••••••"
              error={errors.password}
            />

            {/* Confirm Password */}
            <PasswordInput
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              label="Confirm password"
              placeholder="••••••••"
              error={errors.confirmPassword}
            />

            {/* Terms and Conditions */}
            <div className="flex items-start">
              <div className="flex items-center h-5">
                <input
                  id="terms"
                  name="terms"
                  type="checkbox"
                  checked={termsAgreed}
                  onChange={() => setTermsAgreed(!termsAgreed)}
                  className="focus:ring-red-500 h-4 w-4 text-red-600 border-gray-300 rounded"
                />
              </div>
              <div className="ml-3 text-sm">
                <label htmlFor="terms" className="font-medium">
                  I acknowledge that I have read, understood, and agree to be
                  bound by Vaspay's{" "}
                  <a href="#" className="text-blue-600 hover:text-blue-800">
                    Merchant Services Agreement (MSA)
                  </a>
                  ,{" "}
                  <a href="#" className="text-blue-600 hover:text-blue-800">
                    Terms and Conditions
                  </a>
                </label>
                {errors.terms && (
                  <p className="mt-2 text-sm text-red-600">{errors.terms}</p>
                )}
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={isLoading || !isFormValid}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-500 hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
              >
                {isLoading ? "Creating account..." : "Create your account"}
              </button>
            </div>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">
                  Already have an account?
                </span>
              </div>
            </div>

            <div className="mt-6 text-center">
              <Link
                to="/login"
                className="font-medium text-red-600 hover:text-red-500"
              >
                Login here
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
